import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import { FiSave, FiRefreshCw, FiGlobe, FiImage, FiInfo, FiMail, FiPhone, FiMapPin, FiFacebook, FiInstagram, FiLinkedin, FiTwitter, FiYoutube } from "react-icons/fi";
import {
  FaWhatsapp
} from "react-icons/fa";
import "../styles/WebsiteSettings.css";

export default function WebsiteSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [settings, setSettings] = useState({
    brandName: "",
    aboutTitle: "",
    aboutDescription: "",
    vision: "",
    mission: "",
    contactEmail: "",
    contactPhone: "",
    contactAlternatePhone: "",
    contactAddress: "",
    googleMapEmbedUrl: "",
    facebookUrl: "",
    instagramUrl: "",
    linkedinUrl: "",
    twitterUrl: "",
    youtubeUrl: "",
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/site-settings`);
      const data = await res.json();

      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
        if (data.logo && data.logoContentType) {
          setLogoPreview(`data:${data.logoContentType};base64,${data.logo}`);
        }
      }
    } catch (err) {
      console.error("Failed to load settings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("Logo must be less than 2MB");
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const formData = new FormData();

      Object.keys(settings).forEach(key => {
        formData.append(key, settings[key] || "");
      });

      if (logoFile) formData.append("logo", logoFile);

      const res = await fetch(`${API_BASE_URL}/api/site-settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const json = await res.json();
      if (res.ok) {
        toast.success("Updated successfully");
        fetchSettings();
      } else {
        toast.error(json.message || "Update failed");
      }


    } catch (err) {
      console.error("Save settings error", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="site-settings-loading">Loading website settings...</div>;

  return (
    <div className="site-settings-management">

      {/* HEADER */}
      <div className="site-settings-header">
        <div className="site-settings-header-left">
          <h1>Website Settings</h1>
          <p className="site-settings-page-subtitle">Manage branding, content, contact, and social links</p>
        </div>

        <div className="site-settings-header-right">
          <button className="site-settings-refresh-btn" onClick={fetchSettings}>
            <FiRefreshCw />
            Refresh
          </button>

          <button className="site-settings-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <div className="site-settings-save-spinner" /> : <FiSave />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* SETTINGS SECTIONS */}
      <div className="site-settings-content">

        {/* BRAND SECTION - Column Format */}
        <div className="site-settings-controls">
          <div className="site-settings-tabs-container">
            <button className="site-settings-tab-btn site-settings-tab-active">
              <FiGlobe /> Brand Settings
            </button>
          </div>

          {/* Brand Name and Logo in 2-column layout */}
          <div className="site-settings-form-grid site-settings-brand-grid">
            <div className="site-settings-form-group site-settings-form-full">
              <label>Brand Name</label>
              <input
                type="text"
                name="brandName"
                value={settings.brandName}
                onChange={handleChange}
                placeholder="Your brand name"
                className="site-settings-search-input"

              />
            </div>

            <div className="site-settings-form-group">
              <label>Logo</label>
              <div className="site-settings-logo-card">
                <div className="site-settings-logo-preview">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="site-settings-logo-img" />
                  ) : (
                    <div className="site-settings-logo-placeholder">
                      <FiImage size={48} />
                      <p>Upload your company logo</p>
                      <small>PNG, JPG up to 2MB</small>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="logoInput"
                  accept="image/*"
                  onChange={handleLogoChange}
                  hidden
                />
                <label htmlFor="logoInput" className="site-settings-logo-btn">
                  <FiImage /> Choose File
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* ABOUT SECTION */}
        <div className="site-settings-controls">
          <div className="site-settings-tabs-container">
            <button className="site-settings-tab-btn">
              <FiInfo /> About Content
            </button>
          </div>

          <div className="site-settings-form-grid">
            <div className="site-settings-form-group">
              <label>About Title</label>
              <input
                type="text"
                name="aboutTitle"
                value={settings.aboutTitle}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group site-settings-form-full">
              <label>About Description</label>
              <textarea
                name="aboutDescription"
                value={settings.aboutDescription}
                onChange={handleChange}
                rows={4}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group site-settings-form-full">
              <label>Vision</label>
              <textarea
                name="vision"
                value={settings.vision}
                onChange={handleChange}
                rows={3}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group site-settings-form-full">
              <label>Mission</label>
              <textarea
                name="mission"
                value={settings.mission}
                onChange={handleChange}
                rows={3}
                className="site-settings-search-input"
              />
            </div>
          </div>
        </div>

        {/* CONTACT SECTION */}
        <div className="site-settings-controls">
          <div className="site-settings-tabs-container">
            <button className="site-settings-tab-btn">
              <FiMail /> Contact Info
            </button>
          </div>

          <div className="site-settings-form-grid">
            <div className="site-settings-form-group">
              <label><FiMail /> Email</label>
              <input
                type="email"
                name="contactEmail"
                value={settings.contactEmail}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FiPhone /> Phone</label>
              <input
                type="text"
                name="contactPhone"
                value={settings.contactPhone}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FaWhatsapp /> WhatsApp Number</label>
              <input
                type="text"
                name="contactAlternatePhone"
                value={settings.contactAlternatePhone}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group site-settings-form-full">
              <label><FiMapPin /> Address</label>
              <textarea
                name="contactAddress"
                value={settings.contactAddress}
                onChange={handleChange}
                rows={3}
                className="site-settings-search-input"
              />
            </div>

            {/* Google Map with increased height */}
            <div className="site-settings-form-group site-settings-google-map-input site-settings-form-full">
              <label><FiMapPin /> Google Map Embed URL</label>
              <input
                type="text"
                name="googleMapEmbedUrl"
                value={settings.googleMapEmbedUrl}
                onChange={handleChange}
                className="site-settings-search-input"
                placeholder="https://www.google.com/maps/embed?pb=..."
              />
            </div>
          </div>
        </div>

        {/* SOCIAL SECTION */}
        <div className="site-settings-controls">
          <div className="site-settings-tabs-container">
            <button className="site-settings-tab-btn">
              <FiFacebook /> Social Media
            </button>
          </div>

          <div className="site-settings-form-grid">
            <div className="site-settings-form-group">
              <label><FiFacebook /> Facebook</label>
              <input
                name="facebookUrl"
                value={settings.facebookUrl}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FiInstagram /> Instagram</label>
              <input
                name="instagramUrl"
                value={settings.instagramUrl}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FiLinkedin /> LinkedIn</label>
              <input
                name="linkedinUrl"
                value={settings.linkedinUrl}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FiTwitter /> Twitter</label>
              <input
                name="twitterUrl"
                value={settings.twitterUrl}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>

            <div className="site-settings-form-group">
              <label><FiYoutube /> YouTube</label>
              <input
                name="youtubeUrl"
                value={settings.youtubeUrl}
                onChange={handleChange}
                className="site-settings-search-input"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}